// Supabase client configuration
import { createClient } from '@supabase/supabase-js'
import { SUPABASE_URL, SUPABASE_ANON_KEY, BACKEND_URL } from './config'

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables: SUPABASE_URL and SUPABASE_ANON_KEY must be configured')
}

// Get the current site URL (frontend URL, not backend API URL)
const getFrontendUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  // Fallback: extract domain from backend URL
  if (BACKEND_URL) {
    // For deployed environment, frontend and backend share the same domain
    const url = new URL(BACKEND_URL)
    return url.origin
  }
  return 'http://localhost:3000'
}

const siteUrl = getFrontendUrl()

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  realtime: {
    enabled: false
  }
})

// Helper functions for authentication
export const authHelpers = {
  // Sign up new user
  signUp: async (email, password, userData = {}) => {
    console.log('🔗 Signup redirect URL:', siteUrl)
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${siteUrl}?confirmed=true`
      }
    })
    return { data, error }
  },

  // Sign in user
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  },

  // Sign out user
  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser()
  },

  // Get session
  getSession: () => {
    return supabase.auth.getSession()
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Helper functions for real-time subscriptions
export const realtimeHelpers = {
  // Subscribe to user presence in a room
  subscribeToPresence: (roomId, callback) => {
    return supabase
      .channel(`room:${roomId}:presence`)
      .on('presence', { event: '*' }, callback)
      .subscribe()
  },

  // Subscribe to scene state changes
  subscribeToSceneState: (roomId, callback) => {
    return supabase
      .channel(`room:${roomId}:scene`)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'scene_state',
          filter: `room_id=eq.${roomId}`
        }, 
        callback
      )
      .subscribe()
  },

  // Subscribe to room updates
  subscribeToRoom: (roomId, callback) => {
    return supabase
      .channel(`room:${roomId}`)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'rooms',
          filter: `id=eq.${roomId}`
        }, 
        callback
      )
      .subscribe()
  },

  // Unsubscribe from channel
  unsubscribe: (channel) => {
    return supabase.removeChannel(channel)
  }
}

export default supabase
